import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  QualificationTrainings,
  TrainingQualificationsResponse,
  TrainingsQualificationsWithRelationsResponse,
} from '@wilson/interfaces';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  GetQualificationTrainingsOverview,
  GetQualificationTrainingsOverviewDetails,
  OpenQualificationTrainingsOverviewDetailsDrawer,
  ResetQualificationTrainingsOverviewDetails,
} from './qualification-trainings-v2-overview.action';
import { TrainingQualificationsGateway } from '@wilson/qualification-v2/common';

const defaultPageSize = 50;

export interface QualificationTrainingsOverviewStateModel {
  selectedQualificationTrainingDetails: QualificationTrainings | null;
  qualificationTrainings: QualificationTrainings[];
  totalRecords: number;
  loading: boolean;
  currentPage: number;
  pageSize: number;
  isDrawerVisible: boolean;
}

@State<QualificationTrainingsOverviewStateModel>({
  name: 'qualificationTrainingsOverview',
  defaults: {
    selectedQualificationTrainingDetails: null,
    qualificationTrainings: [],
    totalRecords: 0,
    loading: false,
    currentPage: 1,
    pageSize: defaultPageSize,
    isDrawerVisible: false,
  },
})
@Injectable()
export class TrainingQualificationsV2OverviewState {
  private trainingQualificationsGateway = inject(TrainingQualificationsGateway);

  @Selector()
  static getQualificationTrainings(
    state: QualificationTrainingsOverviewStateModel,
  ): QualificationTrainings[] {
    return state.qualificationTrainings;
  }

  @Selector()
  static getTotalRecords(
    state: QualificationTrainingsOverviewStateModel,
  ): number {
    return state.totalRecords;
  }

  @Selector()
  static getPageSize(state: QualificationTrainingsOverviewStateModel): number {
    return state.pageSize ?? defaultPageSize;
  }

  @Selector()
  static isLoading(state: QualificationTrainingsOverviewStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static qualificationTrainingsById(id: string) {
    return (
      state: QualificationTrainingsOverviewStateModel,
    ): QualificationTrainings | undefined => {
      return state.qualificationTrainings.find(
        (qualificationTraining) => qualificationTraining.id === id,
      );
    };
  }

  @Selector()
  static qualificationTrainingsByIdExists(id: string) {
    return (state: QualificationTrainingsOverviewStateModel): boolean => {
      return state.qualificationTrainings.some(
        (qualificationTraining) => qualificationTraining.id === id,
      );
    };
  }

  @Selector()
  static getQualificationTrainingDetails(
    state: QualificationTrainingsOverviewStateModel,
  ): QualificationTrainings | null {
    return state.selectedQualificationTrainingDetails || null;
  }

  @Selector()
  static isDrawerVisible(
    state: QualificationTrainingsOverviewStateModel,
  ): boolean {
    return state.isDrawerVisible;
  }

  @Action(GetQualificationTrainingsOverview)
  getQualificationTrainings(
    { patchState }: StateContext<QualificationTrainingsOverviewStateModel>,
    { params }: GetQualificationTrainingsOverview,
  ): Observable<TrainingQualificationsResponse> {
    patchState({ loading: true });

    return this.trainingQualificationsGateway
      .getTrainingQualifications(params)
      .pipe(
        tap((response) => {
          patchState({
            qualificationTrainings: response.data,
            totalRecords: response.pagination.totalRecords,
            currentPage: params.pageNumber,
            pageSize: params.pageSize,
            loading: false,
          });
        }),
        catchError((error) => {
          patchState({ loading: false });
          throw error;
        }),
      );
  }

  @Action(GetQualificationTrainingsOverviewDetails)
  getQualificationTrainingsDetails(
    {
      patchState,
      dispatch,
    }: StateContext<QualificationTrainingsOverviewStateModel>,
    {
      qualificationTrainingId,
      userId,
    }: GetQualificationTrainingsOverviewDetails,
  ): Observable<TrainingsQualificationsWithRelationsResponse> {
    patchState({ loading: true });

    return this.trainingQualificationsGateway
      .getTrainingQualificationsDetails(qualificationTrainingId, userId)
      .pipe(
        tap((response) => {
          patchState({
            selectedQualificationTrainingDetails: response.data[0] || null,
            loading: false,
          });
        }),
        tap(() => {
          dispatch(new OpenQualificationTrainingsOverviewDetailsDrawer(true));
        }),
        catchError((error) => {
          patchState({ loading: false });
          throw error;
        }),
      );
  }

  @Action(ResetQualificationTrainingsOverviewDetails)
  resetQualificationTrainingsDetails(
    ctx: StateContext<QualificationTrainingsOverviewStateModel>,
  ): void {
    ctx.patchState({
      selectedQualificationTrainingDetails: null,
    });
  }

  @Action(OpenQualificationTrainingsOverviewDetailsDrawer)
  openDrawer(
    { patchState }: StateContext<QualificationTrainingsOverviewStateModel>,
    { isOpen }: OpenQualificationTrainingsOverviewDetailsDrawer,
  ): void {
    patchState({
      isDrawerVisible: isOpen,
    });
  }
}
